import { t } from 'i18next';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

import {
  Card,
  List,
  Stack,
  Button,
  Select,
  MenuItem,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  InputAdornment,
} from '@mui/material';

import { useConfirmModal } from 'src/hooks/useModal';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import Iconify from 'src/components/iconify';

import { scrollbarStyle } from 'src/sections/style';

import GroupListItem from './GroupListItem';
import { cardTicketModalStyle, listTicketModalStyle } from '../styles';
import updatePermissionListAfterAdding from '../utils/updatePermissionListAfterAdding';

AddGroupsPermissionsForm.propTypes = {
  groupList: PropTypes.array.isRequired,
  availableUserIds: PropTypes.object.isRequired,
  setPermissionUsers: PropTypes.func.isRequired,
  permissionUsers: PropTypes.array.isRequired,
  ticketId: PropTypes.string,
  addMultiplePermissions: PropTypes.func,
  isLocalUpdate: PropTypes.bool,
  hideModal: PropTypes.func,
};

export default function AddGroupsPermissionsForm({
  groupList,
  availableUserIds,
  setPermissionUsers,
  permissionUsers,
  ticketId,
  addMultiplePermissions,
  isLocalUpdate = false,
  hideModal,
}) {
  const [filterGroup, setFilterGroup] = useState('');
  const { isSmallScreen } = useResponsiveScreen();
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [isNotFoundGroup, setIsNotFoundGroup] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState('read');

  const { showConfirmModal } = useConfirmModal(
    t('pages.ticketDetails.confirmPermission'),
    t('pages.ticketDetails.areYouSureToAdd'),
    async () => {
      const users = Array.from(
        new Map(
          selectedGroups.flatMap((group) => group.users).map((user) => [user.id, user])
        ).values()
      );
      const allUserIds = selectedGroups.flatMap((group) =>
        group.users.map((user) => user.id)
      );

      const successCallback = () => {
        // Update users list after API success
        const updateUsers = updatePermissionListAfterAdding({
          users,
          permission: selectedPermission,
          availableUserIds,
          permissionUsers,
        });
        setPermissionUsers(updateUsers);
      };

      // Call API to add multiple users
      addMultiplePermissions(
        ticketId,
        allUserIds,
        selectedPermission === 'read',
        selectedPermission === 'write',
        successCallback
      );
    }
  );

  const handleAddPermissionClick = () => {
    const users = Array.from(
      new Map(
        selectedGroups.flatMap((group) => group.users).map((user) => [user.id, user])
      ).values()
    );

    // Local state update only
    const updateUsers = updatePermissionListAfterAdding({
      users,
      permission: selectedPermission,
      availableUserIds,
      permissionUsers,
    });
    setPermissionUsers(updateUsers);
    hideModal();
  };
  const handlePermissionChange = (event) => {
    setSelectedPermission(event.target.value);
  };

  const handleFilterByGroup = (event) => {
    setFilterGroup(event.target.value);
  };

  const filteredGroups = useMemo(() => {
    const lowerCaseFilterName = filterGroup.toLowerCase();
    const filteredData = groupList.filter((group) =>
      group.name.toLowerCase().includes(lowerCaseFilterName)
    );

    setIsNotFoundGroup(!filteredData.length && !!filterGroup);
    return filteredData;
  }, [groupList, filterGroup]);

  return (
    <Stack px={2} spacing={2} my={2}>
      <TextField
        value={filterGroup}
        onChange={handleFilterByGroup}
        placeholder={`${t('filters.search')}`}
        size={isSmallScreen ? 'small' : 'medium'}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify
                icon="eva:search-fill"
                sx={{
                  color: 'text.disabled',
                  width: isSmallScreen ? 16 : 20,
                  height: isSmallScreen ? 16 : 20,
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          '& input': {
            fontSize: isSmallScreen ? '0.775rem' : '1rem',
          },
        }}
      />
      <Card
        sx={{
          ...cardTicketModalStyle,
          height: isSmallScreen ? '200px' : '350px',
        }}
      >
        <List sx={{ ...listTicketModalStyle, pl: 1.5 }}>
          {filteredGroups.length > 0 ? (
            filteredGroups.map((group, index) => (
              <GroupListItem
                key={index}
                group={group}
                setSelectedGroups={setSelectedGroups}
                selectedGroups={selectedGroups}
                availableUserIds={availableUserIds}
              />
            ))
          ) : (
            <Typography
              textAlign="center"
              variant="subtitle1"
              sx={{
                fontSize: isSmallScreen ? '0.675rem' : '1rem',
              }}
            >
              {isNotFoundGroup
                ? t('pages.ticketDetails.noGroupsFound')
                : t('pages.ticketDetails.noGroupsAvailable')}
            </Typography>
          )}
        </List>
      </Card>
      <FormControl>
        <InputLabel
          id="ticket-status-select-label"
          sx={{
            fontSize: isSmallScreen ? '0.675rem' : '1rem',
          }}
        >
          {t('pages.ticketDetails.permission')}
        </InputLabel>
        <Select
          labelId="ticket-status-select-label"
          id="status-select"
          name="status_id"
          label="Permission"
          value={selectedPermission}
          onChange={handlePermissionChange}
          size={isSmallScreen ? 'small' : 'medium'}
          MenuProps={{
            PaperProps: {
              sx: {
                ...scrollbarStyle,
              },
            },
          }}
        >
          <MenuItem
            value="read"
            sx={{
              fontSize: isSmallScreen ? '0.675rem' : '1rem',
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem' }}
            >
              {t('pages.ticketDetails.read')}
            </Typography>
          </MenuItem>
          <MenuItem
            value="write"
            sx={{
              fontSize: isSmallScreen ? '0.675rem' : '1rem',
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem' }}
            >
              {t('pages.ticketDetails.modify')}
            </Typography>
          </MenuItem>
        </Select>
      </FormControl>
      <Button
        variant="outlined"
        onClick={
          selectedGroups.length !== 0 && isLocalUpdate
            ? handleAddPermissionClick
            : showConfirmModal
        }
        disabled={selectedGroups.length === 0}
        sx={{
          fontSize: isSmallScreen ? '0.675rem' : '1rem',
        }}
      >
        {t('pages.ticketDetails.addUserPermission')}
      </Button>
    </Stack>
  );
}
